import { MAX_EXPORT_ROWS, } from '@common/dataExport';
import { selectExportColumns } from './dataExporter';
const CALL_COLUMNS = {
    id: 'Call ID', entity_id: 'Contact ID', contact: 'Contact name', job_id: 'Job ID', company_id: 'Company ID',
    user_name: 'Team member', direction: 'Direction', status: 'Status', created_at: 'Call date (UTC)',
    duration_seconds: 'Duration (seconds)', outcome: 'Outcome', notes: 'Call notes',
    summary: 'AI summary', next_steps: 'AI next steps', summary_status: 'AI summary status', transcript_status: 'Transcript availability',
    recording_status: 'Recording status', recording_url: 'Recording link (sign-in required)',
    expires_at: 'Capture expires (UTC)',
};
const TRANSCRIPT_COLUMNS = {
    call_id: 'Call ID', entity_id: 'Contact ID', contact: 'Contact name', key: 'Line ID',
    at: 'Spoken at (UTC)', speaker: 'Speaker', text: 'Text',
};
function columns(labels) {
    return Object.entries(labels).map(([id, label]) => ({ id, label }));
}
export const VOICE_EXPORT_DATASETS = [
    {
        id: 'calls', label: 'Calls', columns: columns(CALL_COLUMNS),
        defaultColumns: [
            'id', 'entity_id', 'contact', 'user_name', 'created_at', 'status', 'duration_seconds', 'outcome',
            'transcript_status', 'recording_status', 'recording_url', 'expires_at',
        ],
    },
    { id: 'transcripts', label: 'Transcripts (final speaker turns)', columns: columns(TRANSCRIPT_COLUMNS), defaultColumns: [] },
];
export async function loadVoiceExportTables(adapter, request, progress) {
    const wanted = request.selections.filter((selection) => selection.dataset === 'calls' || selection.dataset === 'transcripts');
    if (wanted.length === 0)
        return [];
    const calls = { id: 'calls', label: 'Calls', columns: columns(CALL_COLUMNS), rows: [] };
    const transcripts = { id: 'transcripts', label: 'Transcripts', columns: columns(TRANSCRIPT_COLUMNS), rows: [] };
    const wantsTranscripts = wanted.some((selection) => selection.dataset === 'transcripts');
    const seenCalls = new Set();
    let rows = 0;
    const append = (table, row) => {
        rows += 1;
        if (rows > MAX_EXPORT_ROWS)
            throw new Error('Too many call export rows. Narrow the selection or call date range.');
        table.rows.push(row);
    };
    for (const [index, entityId] of [...new Set(adapter.entityIds)].entries()) {
        let after = '';
        const cursors = new Set();
        for (;;) {
            progress.signal.throwIfAborted();
            progress.report(`Loading calls: contact ${index + 1} of ${adapter.entityIds.length}...`);
            const page = await adapter.calls({
                entity_id: entityId, job_id: adapter.jobId, after, from: request.from, to: request.to,
            });
            progress.signal.throwIfAborted();
            for (const call of page.calls) {
                if (seenCalls.has(call.id))
                    continue;
                seenCalls.add(call.id);
                const name = adapter.contactName(call.entity_id);
                const summary = call.summary?.status === 'ready' ? call.summary : null;
                append(calls, [
                    call.id, call.entity_id, name, call.job_id, call.company_id, call.user_name, call.direction,
                    call.status, new Date(call.created_at).toISOString(), call.duration_seconds, call.outcome, call.notes,
                    summary?.text ?? '', summary?.next_steps.join('\n') ?? '', call.summary?.status ?? 'unavailable',
                    call.transcript_status,
                    call.recording_status, call.recording_available ? adapter.recordingUrl(call.id) : '',
                    call.expires_at === null ? '' : new Date(call.expires_at).toISOString(),
                ]);
                if (!wantsTranscripts || call.transcript_status !== 'available')
                    continue;
                let lineAfter = '';
                const lineCursors = new Set();
                for (;;) {
                    progress.signal.throwIfAborted();
                    progress.report(`Loading transcript for ${name || call.entity_id}...`);
                    const transcript = await adapter.transcript(call.id, lineAfter);
                    progress.signal.throwIfAborted();
                    for (const line of transcript.lines) {
                        append(transcripts, [
                            call.id, call.entity_id, name, line.key, new Date(line.at).toISOString(),
                            line.speaker === 'seat' ? adapter.teamSpeaker : adapter.contactSpeaker, line.text,
                        ]);
                    }
                    if (transcript.next === null)
                        break;
                    if (lineCursors.has(transcript.next))
                        throw new Error('Transcript paging stopped advancing. Try again.');
                    lineCursors.add(transcript.next);
                    lineAfter = transcript.next;
                }
            }
            if (page.next === null)
                break;
            if (cursors.has(page.next))
                throw new Error('Call paging stopped advancing. Try again.');
            cursors.add(page.next);
            after = page.next;
        }
    }
    const tables = wanted.map((selection) => selectExportColumns(selection.dataset === 'calls' ? calls : transcripts, selection));
    if (!wanted.some((selection) => selection.dataset === 'calls')) {
        tables.push(selectExportColumns(calls, {
            dataset: 'calls', columns: ['id', 'entity_id', 'contact', 'transcript_status', 'recording_status'],
        }));
    }
    return tables;
}
